/* eslint-disable */
import React, { useEffect } from 'react';
import { Button, Input, Typography } from 'antd';
import { useHistory, useParams } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Title } from '~/components';
import { smsOtp, sendCompanyValidate } from '~/api/validate';
import paths from '~/pages/paths';
import colors from '~/constants/colors';
import { ConfirmButton } from '~/layouts/ksa/CompanyInfoLayout';
import theme from '~/constants/theme';
import Form from './components/Form';
import { selectStep } from '~/api/wizard';
import { useGetApplication, useSelectStep } from '~/controllers/wizard';
import ContentHeader from '~/components/ContentHeader';
import WizardContent from '~/layouts/WizardContent';
import ApplicationSummary from '~/components/ApplicationSummary';
import ApplicationNavigation from '~/layouts/uae/ApplicationNavigation';
import OtpInput from '~/pages/ksa/dataValidation/OtpInput';
import { useIsMobile, useIsTablet } from '~/hooks/useScreenSizes';
import TextWithTimer from '~/pages/ksa/dataValidation/TextWithTimer';
import { getpersonalIdentificationType } from '~/utils/textOnlyValidation';
import { message } from 'antd';
import { getTrackingId } from '~/utils/handleGetUtm';

const SmsOtpPage: React.FC = () => {
  const applicationQuery = useGetApplication();
  const history = useHistory();
  const [showError, setShowError] = React.useState(false);
  const params = useParams<any>();
  const selectStep = useSelectStep();
  const [isLoading, setIsLoading] = React.useState(false);
  const { t } = useTranslation('ksaApplication');
  const { t: tc } = useTranslation('ksaApplication');
  const [form] = Form.useForm();

  const [otpValue, setOtpValue] = React.useState('');

  const onSubmitCode = async ({ code }: { code: string }) => {
    setIsLoading(true);

    try {
      await smsOtp({
        challengeId: params.smsOtpChallengeId,
        smsOtpCode: code,
      });

      (window as any).dataLayer.push({
        event: 'event_formsubmit',
        page_ln: 'en',
        page_name: window.location.href,
        page_source: document.referrer || 'direct',
        page_section: 'Verify Number',
        trackingId: getTrackingId(applicationQuery?.data),
      });

      await selectStep.mutateAsync({ stepKey: 'application.companyInformation', stageKey: 'ksaApplication' });
    } catch (error: any) {
      // OTP validation error
      if (error?.response) {
        (window as any).dataLayer.push({
          event: 'event_fielderror',
          page_ln: 'en',
          page_name: window.location.href,
          page_source: document.referrer || 'direct',
          form_fieldname: 'otp',
          form_fielderror: 'otp invalid',
          trackingId: getTrackingId(applicationQuery?.data),
        });
        setShowError(true);
      }
      setIsLoading(false);
    }
  };
  const isMobile = useIsMobile();
  const location = useHistory<{
    crNumberOrEntityNumber?: string;
    personalIdentificationType?: string;
    personalIdentificationNumber?: string;
    personalPhoneNumber?: string;
  }>();

  const onResendCode = () => {
    // Get the stored form data from route state
    const validationData = location.location.state;

    if (
      !validationData?.crNumberOrEntityNumber ||
      !validationData?.personalIdentificationNumber ||
      !validationData?.personalPhoneNumber ||
      !validationData?.personalIdentificationType
    ) {
      message.error(t('Unable to resend code. Please try again from the beginning.'));
      return;
    }

    const {
      crNumberOrEntityNumber,
      personalIdentificationType,
      personalIdentificationNumber,
      personalPhoneNumber,
    } = validationData;

    // Prevent multiple submissions
    if (isLoading) {
      console.log('Resend blocked - already loading');
      return;
    }

    setIsLoading(true);

    void sendCompanyValidate({
      crNumberOrEntityNumber,
      personalIdentificationType,
      personalIdentificationNumber,
      personalPhoneNumber,
    })
      .then((res) => {
        // Update the URL with the new challenge ID if needed
        history.replace(
          paths.ksa.dataValidation.smsotp
            .replace(':smsOtpChallengeId', res.smsOtpChallengeId)
            .replace(':mobileNumber', personalPhoneNumber.replace(/.(?=.{3})/g, '*')),
          validationData
        );

        message.success(t('Verification code has been resent'));

        (window as any).dataLayer.push({
          event: 'event_verifyr',
          page_ln: 'en',
          page_name: window.location.href,
          page_source: document.referrer || 'direct',
          verify_resend: 'Resend Mobile OTP',
        });

        setIsLoading(false);
      })
      .catch((error) => {
        console.log('Resend error:', error?.response?.data);
        setIsLoading(false);
        message.error(t('Failed to resend code. Please try again.'));

        (window as any).dataLayer.push({
          event: 'event_fielderror',
          page_ln: 'en',
          page_name: window.location.href,
          page_source: document.referrer || 'direct',
          form_fieldname: 'resendOtp',
          form_fielderror: error?.response?.data?.description,
        });
      });
  };

  return (
    <WizardContent
      left={
        <>
          <ApplicationNavigation />
        </>
      }
      content={
        <Form
          validateTrigger="onSubmit"
          form={form}
          style={{ backgroundColor: isMobile ? 'transparent' : undefined }}
          name="dataValidationSmsOtpForm"
          onFinish={onSubmitCode}
          onValuesChange={(changedValues, allValues) => {
            setOtpValue(allValues.code || '');
          }}
        >
          <ContentHeader
            title={t('Verify Number')}
            subtitle={
              t(`Enter the code sent to the mobile number ending in`) + params?.mobileNumber
            }
            hideBackArrow={true}
          />
          <div style={{ width: useIsTablet() ? '100%' : '80%' }}>
            <div>
              <Typography.Title style={{ marginBottom: 6 }} level={5}>
                {t('Verification code')}
              </Typography.Title>
              <FormItem
                preserve={true}
                name="code"
                rules={[{ required: true, message: t('Please enter SMS OTP') }]}
              >
                <OtpInput hasError={showError} name="code" length={6} />
              </FormItem>
            </div>
            {showError && (
              <Typography.Paragraph className="error-message">
                {t('Invalid OTP')}
              </Typography.Paragraph>
            )}
            <div style={{ marginTop: 0 }}>
              <TextWithTimer cooldownTime={90} onClick={onResendCode} />
            </div>

            <StyledConfirmButton
              id="dataValidationSmsOtpForm_submit"
              type="primary"
              htmlType="submit"
              loading={isLoading}
              disabled={otpValue.length < 1}
            >
              {tc('Verify')}
            </StyledConfirmButton>

            {/* <Container> */}
            {/*   <Trans t={t} i18nKey="Didn't receive any code? <1>Try again</1>"> */}
            {/*     {"Didn't receive any code? "} */}
            {/*     /!* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events *!/ */}
            {/*     <a */}
            {/*       role="button" */}
            {/*       onClick={() => { */}
            {/*         history.goBack(); */}
            {/*       }} */}
            {/*     > */}
            {/*       Try again */}
            {/*     </a> */}
            {/*   </Trans> */}
            {/* </Container> */}
          </div>
        </Form>
      }
      right={<ApplicationSummary />}
    />
  );
};

const Container = styled.div`
  margin-top: 32px;
  color: ${colors.textColor};
  span {
    color: ${colors.textColor};
  }
`;

const InputValue = styled(Input)`
  &[dir='rtl'] {
    direction: ltr;
    text-align: right;
  }
`;

const FormInput = styled.div`
  height: 75px;
  margin-bottom: 24px;
`;

const FormItem = styled(Form.Item as any)`
  @media (max-width: ${theme.breakpoints.md}px) {
    max-width: 100% !important;
  }
`;

const StyledConfirmButton = styled(ConfirmButton)`
  height: 56px;
  max-width: 396px; /* matches 6 inputs of 56px + 5 gaps of 12px = 396px */
  width: 100%;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;

  @media (max-width: ${theme.breakpoints.md}px) {
    max-width: unset;
  }
`;

export default SmsOtpPage;
